/** @jsxImportSource @emotion/react */
import { CircularProgress, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { useTranslation } from 'react-i18next';
import OpenEntriesService from '../../../service/OpenEntriesService';
import { useParams } from 'react-router-dom';
import { IOpenEntriesModel, IOpenEntriesPostsModel } from '../../../service/model/IOpenEntriesModel';
import ErrorMessageContainer from '../../../common/components/ErrorMessageContainer';
import EmptyTableMessageContainer from '../../../common/components/EmptyTableMessageContainer';

interface IProps {
    //rows: ISourceDocumentModel[] | null,
    //errors: string[],
    //limitPerPage: number,
    //totalItems: number,
    //loader: boolean,
    //changePage: (page: number) => void
    //sortItem: (sortItem: IFileSortItem | null) => void,
}

interface IRows {
    openEntry: IOpenEntriesModel,
}
interface ISubRows {
    open: boolean,
    id: number,
    posts: IOpenEntriesPostsModel[],
}

const TableOpenEntries = (props: IProps): JSX.Element => {

    const { organizationId, userType } = useParams();

    const { t } = useTranslation();

    const [tableRows, setTableRows] = useState<IOpenEntriesModel[]>([]);
    const [tableRowsError, setTableRowsError] = useState(false);
    const [loader, setLoader] = useState(true);


    useEffect(() => {
        let isMounted = true; //Avoid memory leaks. Async function running after dismount.

        setLoader(true);

        const service = new OpenEntriesService();

        const getCustomers = async () => {
            const result = await service.getCustomers(organizationId);
            if (result.hasErrors) {
                setTableRowsError(true);
            } else {
                setTableRowsError(false);
                if (isMounted) {
                    setTableRows(result.value);
                }
            }
            setLoader(false);
        };
        const getSuppliers = async () => {
            const result = await service.getSuppliers(organizationId);
            if (result.hasErrors) {
                setTableRowsError(true);
            } else {
                setTableRowsError(false);
                if (isMounted) {
                    setTableRows(result.value);
                }
            }
            setLoader(false);
        };

        if (userType === "Customer")
            getCustomers();
        else if (userType === "Supplier")
            getSuppliers();

        return () => { isMounted = false };
    }, [organizationId, userType]);

    const CreateRow = (props: IRows): JSX.Element => {

        const [open, setOpen] = useState(false);

        return <>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell colSpan={9} >
                    <Typography fontWeight="bold" variant="body1">{props.openEntry.actorName}</Typography>
                    <Stack direction="row" alignItems="center">
                        <Typography variant="body2" marginRight="1rem">{t("reports.sumOutstanding") + ": "}</Typography>
                        <Typography fontWeight="bold" variant="body1" marginRight="1rem">
                            {new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(props.openEntry.outstanding) } 
                        </Typography>
                        <Typography variant="body2" marginRight="1rem">{t("reports.sumOutstandingOverdue") + ": "}</Typography>
                        <Typography fontWeight="bold" variant="body1" marginRight="1rem">
                            {new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(props.openEntry.outstandingOverdue)} 
                        </Typography>
                    </Stack>
                </TableCell>
            </TableRow>
            <CreateSubRows open={open} id={props.openEntry.actorId} posts={props.openEntry.openEntries} />
        </>
    }

    const CreateSubRows = (props: ISubRows): JSX.Element => {

        return <>
            {props.posts.map(e => {
                const voucherDate = new Date(e.voucherDate);
                const amount = new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(e.amount);
                const outstanding = new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(e.outstanding);
                const dueDate = new Date(e.dueDate);

                return <TableRow sx={{ display: props.open ? "table-row" : "none" }}>
                    <TableCell></TableCell>
                    <TableCell component="th" scope="row">{props.id}</TableCell>
                    <TableCell align="right">{e.invoiceNo}</TableCell>
                    <TableCell align="right">{e.voucherId}</TableCell>
                    <TableCell align="right">{voucherDate.toLocaleDateString()}</TableCell>
                    <TableCell align="right">{e.standardText}</TableCell>
                    <TableCell align="right">{amount}</TableCell>
                    <TableCell align="right">{outstanding}</TableCell>
                    <TableCell align="right">{dueDate.toLocaleDateString()}</TableCell>
                    <TableCell align="right">{e.remitted}</TableCell>
                </TableRow>

            })}
        </>
    }

    if (userType !== "Customer" && userType !== "Supplier") {
        return <Typography>{t("reports.reportDoesNotExist")}</Typography>

    } else {

        if (loader) {
            return <CircularProgress />
        } else if (tableRowsError) {
            return <ErrorMessageContainer />
        } else if (tableRows.length < 1) {
            return <EmptyTableMessageContainer />
        } else {
            return <TableContainer sx={{ height: "100%" }}>
                <Table size="small" aria-label="collapsible table" stickyHeader sx={{ width: "100%", height: "100%" }}>
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell>{userType === "Customer" ? t("reports.customerId") : t("reports.supplierId")}</TableCell>
                            <TableCell align="right">{t("reports.invoiceNo")}</TableCell>
                            <TableCell align="right">{t("reports.voucherNo")}</TableCell>
                            <TableCell align="right">{t("reports.voucherDate")}</TableCell>
                            <TableCell align="right">{t("reports.standardText")}</TableCell>
                            <TableCell align="right">{t("search.amount")}</TableCell>
                            <TableCell align="right">{t("reports.outstanding")}</TableCell>
                            <TableCell align="right">{t("search.dueDate")}</TableCell>
                            <TableCell align="right">{t("reports.remitted")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableRows.length > 0 ? tableRows.map(e => <CreateRow openEntry={e} />) : <></>}
                    </TableBody>
                </Table>
            </TableContainer>
        }
    }

    
}

export default TableOpenEntries;